import { Autocomplete, TextField } from '@mui/material';
import React from 'react';
import { useThunk } from './useThunk';

/**
 * OrderAutocomplete Component
 * 
 * A reusable autocomplete component for searching orders by ID.
 * It encapsulates the fetching logic and handles debounce internally via useThunk hook.
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Selected order ID
 * @param {Function} props.onChange - Callback when value changes (newValue: string | object)
 * @param {Function} props.onSelect - Callback when value is selected (newValue: object | string)
 * @param {string|number} props.organisationId - Organization ID to filter orders
 * @param {Function} props.setError - Callback to show errors
 * @param {string} props.label - Input label
 * @param {string} props.placeholder - Input placeholder
 * @param {boolean} props.required - Whether the field is required
 */
const OrderAutocomplete = ({
    value,
    onChange,
    onSelect,
    organisationId,
    setError,
    label = 'Order #',
    placeholder,
    required = false,
    ...rest
}) => {
    const {
        options,
        loading,
        inputValue,
        handleInputChange,
    } = useThunk({ value, organisationId, setError });

    const handleInternalChange = (event, newValue) => {
        if (newValue && typeof newValue === 'object') {
            onChange(String(newValue.id));
            if (onSelect) onSelect(newValue);
        } else {
            onChange(newValue || '');
            if (onSelect) onSelect(newValue);
        }
    };

    return (
        <Autocomplete
            {...rest}
            freeSolo
            options={options}
            loading={loading}
            inputValue={inputValue}
            noOptionsText={inputValue.length < 4 ? 'Type at least 4 characters' : 'No orders found'}
            getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.id ? String(option.id) : '';
            }}
            onInputChange={handleInputChange}
            onChange={handleInternalChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    variant='outlined'
                    placeholder={!organisationId ? 'Select Organisation first' : (placeholder || 'Search Order #')}
                    onFocus={() => {
                        if (!organisationId && setError) {
                            setError('Please select an organisation first');
                        }
                    }}
                />
            )}
        />
    );
};

export default OrderAutocomplete;
